<template>
  <drag-resize type="addNameManager" :drag-box-style="dragBoxStyle" :drag-box-head-style="dragBoxHeadStyle">
    <template #header>
      <span class="name-range__title">{{ $t("nameRange.addNameRange") }}</span>
      <i class="icon-close" @click="closeModal"></i>
    </template>

    <template #body>
      <div v-if="showOtherTableParts" ref="dialog" class="name-range__dialog" data-test-id="add-name-range-dialog">
        <div class="name-range__field name-range__field--name">
          <label for="name">Name</label>
          <input
            id="name"
            ref="nameInput"
            v-model="name"
            type="text"
            placeholder="Enter Name"
            :disabled="isEditMode && disableName"
            data-test-id="name-for-named-range"
            tabindex="0"
          />
        </div>

        <div
          ref="scopeInput"
          tabindex="0"
          class="name-range__field name-range__field--scope"
          @focus="toggleDropdown(true)"
          @blur="toggleDropdown(false)"
          @click="toggleDropdown(true)"
        >
          <label for="">Scope</label>

          <select-option v-if="showDropdown" :options="scopeList" :default="scope" @input="scope = $event" />

          <div class="selected">
            <span> {{ scope }} </span>
            <i :class="showDropdown ? icons.MenuDown : icons.MenuUp"></i>
          </div>
        </div>

        <div class="name-range__field name-range__field--refers-to">
          <label for="refers-to">Refers to</label>
          <range-selector ref="referInput" :attrs="rangeSelectorAttrs" tabindex="0" @passRange="refersTo = $event" />
          <custom-icon
            :class-name="rangeSelectorCollapse ? icons.ChevronDown : icons.ChevronUp"
            class="name-range__toggle"
            @click="toggleSelector"
          />
        </div>

        <div class="name-range__field name-range__field--comment">
          <label for="comment">Comment</label>
          <textarea
            id="comment"
            ref="commentInput"
            v-model="comment"
            placeholder="Add a comment"
            data-test-id="comment-for-named-range"
            tabindex="0"
          ></textarea>
        </div>
      </div>

      <div v-else ref="dialog" class="name-range__dialog">
        <div class="name-range__field name-range__field--refers-to">
          <label for="refers-to">Refers to</label>
          <range-selector :attrs="rangeSelectorAttrs" tabindex="0" @passRange="refersTo = $event" />
          <custom-icon
            :class-name="rangeSelectorCollapse ? icons.ChevronDown : icons.ChevronUp"
            class="name-range__toggle"
            @click="toggleSelector"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="name-range__actions">
        <custom-button
          button-type="button"
          button-class="btn btn-default"
          class="name-range__button name-range__button--cancel"
          tabindex="0"
          @click="closeModal"
        >
          {{ $t("actions.cancel") }}
        </custom-button>

        <custom-button
          class="name-range__button name-range__button--submit"
          button-type="button"
          button-class="btn btn-default"
          :disabled="!isFormValid"
          data-test-id="submit-named-range"
          tabindex="0"
          @click="submitForm"
        >
          {{ $t("actions.ok") }}
        </custom-button>
      </div>
    </template>
  </drag-resize>
</template>

<script>
import DesignerMixin from "../DesignerMixin";
import { convertToAbsolute, removePartUntilSymbol } from "../../../utils/util";
import { rangeIndexToLabel } from "../cell/Utils";
import rangeSelector from "../../commons/RangeSelector.vue";
import CustomIcon from "../../commons/CustomIcon.vue";
import { icons } from "../../../utils/icons";
import CustomButton from "../../commons/CustomButton.vue";
import SelectOption from "../../commons/SelectOption.vue";
import DragResize from "../../commons/DragResize.vue";

export default {
  components: {
    // CustomModal,
    rangeSelector,
    CustomIcon,
    CustomButton,
    SelectOption,
    DragResize,
  },
  mixins: [DesignerMixin],
  data() {
    return {
      showDropdown: false,
      icons,
      expressionSheetId: null,
      name: "",
      defaultName: "",
      scope: "Workbook",
      comment: "",
      refersTo: "",
      disableName: false,
      rangeSelectorAttrs: {
        placeholder: "Enter range or formula",
        type: "text",
      },
    };
  },
  computed: {
    dragBoxHeadStyle() {
      return { marginBottom: "1.5rem" };
    },
    dragBoxStyle() {
      return { backgroundColor: "#fdfeff" };
    },
    showOtherTableParts() {
      return this.rangeSelectorCollapse && !this.startDragging;
    },
    scopeList() {
      return ["Workbook", ...this.allSheets.map((x) => x.name)];
    },
    initValues() {
      return this.$store.state.table.editingNameRange || {};
    },
    isEditMode() {
      return this.$store.state.table.editingNameRange !== null;
    },
    listOfDefaultNames() {
      return this.$store.getters["table/getAllTableAndNamedRanges"]();
    },
    listOfLocalNames() {
      let locals = [];
      this.listOfDefaultNames.forEach((item) => {
        const localElement = this.$store.getters["table/getLocalNameRangeByDefault"](item.name);
        locals.push(localElement);
      });
      return locals;
    },
    isFormValid() {
      const validNameRegex = /^[a-zA-Z]([a-zA-Z_]*(_[0-9]+)*)*$/;
      const isNameValid = validNameRegex.test(this.name);

      if (!isNameValid) return false;

      if (this.defaultName === this.name) return true;
      const isUniqueName = !this.listOfLocalNames.some((item) => item.toLowerCase() === this.name.toLocaleLowerCase());

      return isUniqueName;
    },
  },
  mounted() {
    if (!this.rangeSelectorCollapse) this.$store.dispatch("cells/setRangeSelectorCollapse", true);
    window.addEventListener("keydown", this.handleKeydown);
    document.addEventListener("keydown", this.trapFocus);

    this.$nextTick(() => {
      if (this.$refs.nameInput && !this.isEditMode) {
        this.$refs.nameInput.focus();
      }
    });

    if (this.isEditMode) {
      this.initializeFields();
    } else {
      const sheet = this.$store.state.sheets.currentSheet;
      const rangeValue = this.$store.getters["cells/activeRange"]().range;
      this.refersTo = `=${sheet.name}!${convertToAbsolute(rangeIndexToLabel(rangeValue))}`;
    }
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKeydown);
    document.removeEventListener("keydown", this.trapFocus);
  },
  methods: {
    toggleDropdown(value) {
      this.showDropdown = value;
    },
    toggleSelector() {
      const value = this.$store.state.cells.rangeSelectorCollapse;
      this.$store.dispatch("cells/setRangeSelectorCollapse", !value);
    },
    closeModal() {
      this.$store.dispatch("table/setAddNamedRangeVisibility", false);

      if (this.$store.state.table.addNameSource === "nameManager") {
        this.$store.dispatch("table/setNameManagerVisibility", true);
        this.$store.dispatch("table/setAddNameSource", null);
        this.$store.dispatch("table/setEditingNameRange", null);
      }
    },
    initializeFields() {
      this.name = this.initValues.name.value || "";
      this.scope = this.initValues.scope.value || "Workbook";
      this.comment = this.initValues.comment || "";
      this.refersTo = this.initValues.refer.value || "";
      this.expressionSheetId = this.initValues.sheetId;
      this.disableName = this.initValues.disableName || false;
      this.defaultName = this.name || "";
    },
    async submitForm() {
      if (!this.refersTo) {
        this.refersTo = this.initValues.refer.value || "";
      }
      this.scope = this.allSheets.findIndex((x) => x.name === this.scope);

      if (this.scope < 0) {
        this.scope = undefined;
      }
      if (!this.expressionSheetId) {
        this.expressionSheetId = this.sheetId ?? this.$store.state.sheets.currentSheet.id;
      }

      if (!this.isEditMode) {
        if (this.defaultName.length < 1) {
          this.defaultName = this.name;
        }
        let uniqueName = this.defaultName || this.name;
        let counter = 1;
        const listOfNames = this.listOfDefaultNames;

        while (listOfNames.some((element) => element.name === uniqueName)) {
          uniqueName = `${this.defaultName || "nameRange"}_${counter}`;
          counter++;
        }
        this.defaultName = uniqueName;
      } else {
        this.defaultName = await this.$store.getters["table/getDefaultNameRangeByLocal"](this.defaultName);
      }
      await this.$store.dispatch("table/updateNamedExpression", {
        defaultName: this.defaultName.toLowerCase(),
        localName: this.name.toLowerCase(),
        sheetId: this.expressionSheetId,
        expression: removePartUntilSymbol(this.refersTo, "!"),
        scope: this.scope,
        type: "namedRange",
        comment: this.comment,
      });
      setTimeout(() => {
        this.$store.dispatch("corners/loadCorners", null, { root: true });
      }, 0);
      await this.$store.dispatch("table/persistNamedExpressions");
      this.closeModal();
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        this.closeModal();
      } else if (event.key === "Enter" && this.isFormValid) {
        this.submitForm();
      }
    },
    trapFocus(e) {
      const dialog = this.$refs.dialog;
      if (!dialog) return;
      if (!dialog.contains(document.activeElement)) return;
      if (e.key !== "Tab") return;
      const focusable = dialog.querySelectorAll(
        'input:not([disabled]),dev, select, textarea,[tabindex="0"], button:not([disabled]), [tabindex="0"]:not([disabled]), [tabindex="1"]:not([disabled])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables.less";

.selected {
  cursor: pointer;
  user-select: none;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  outline: none;
}

.icon-close {
  cursor: pointer;
}

.name-range__dialog {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  height: 100%;
}

.name-range__field {
  position: relative;
  border-radius: 3px;
  border: 1px solid rgba(8, 50, 119, 0.13);

  &:focus-within {
    border: 1px solid @kk-color-royal-blue;
  }

  input,
  textarea {
    width: 100%;
    color: @kk-color-primary-blue;
    padding: 5px 10px;

    &::placeholder {
      color: #6e8cbb;
      font-size: 12px;
    }

    &:focus {
      border: none;
      outline: none;
    }
  }

  label {
    position: absolute;
    top: -10px;
    left: 8px;
    font-size: 12px;
    color: @kk-color-royal-blue;
    padding: 0 5px;
    background-color: #fdfeff;
  }
}

.name-range__actions {
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 1rem;
}

.name-range__title {
  font-weight: 500;
}

.name-range__toggle {
  top: 50%;
  position: absolute;
  transform: translate(0%, -50%);
  right: 10px;
}

.name-range__field--scope {
  padding: 5px 0px 5px 10px;
  padding-left: 0px;
  outline: none;
}

.name-range__field--comment {
  padding: 0;
  height: 7rem;
  min-height: 2rem;
  flex: 1;

  textarea {
    resize: none;
    overflow: hidden;
  }
}

.name-range__button {
  width: 130px;
  text-align: center;
  border-radius: 3px;
}

.name-range__button--cancel {
  background: rgba(196, 206, 221, 0.3);
  color: @kk-color-primary-blue;
}

.name-range__button--submit {
  background: @kk-color-royal-blue;
  color: @kk-color-white;
}
</style>
